import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AppState, Platform, ScrollView, Share, StyleSheet, View } from 'react-native';
import { ActivityIndicator, Button, Card, Chip, Snackbar, Text, TextInput, useTheme } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'expo-router';
import * as WebBrowser from 'expo-web-browser';
import { useAuth } from '@/src/context/AuthContext';
import { apiFetch } from '@/src/api/apiService';
import { API_BASE } from '@/src/api/config';
import { StandardTerms, type StandardTermsData } from '@/src/components/referrals/StandardTerms';
import { MetricsCard, type PartnerSummary } from '@/src/components/referrals/MetricsCard';
import { ReferralCodeCard } from '@/src/components/referrals/ReferralCodeCard';
import { ConnectCard, type ConnectReadiness } from '@/src/components/referrals/ConnectCard';
import { safeShareUrl, safeStripeUrl } from '@/src/components/referrals/offerCatalogue';
import { Logo } from '@/src/components/Logo';

interface Workspace {
  display_name: string; status: string; financial_status: string; attribution_ready: boolean;
  vat_regime?: string; current_terms?: StandardTermsData | null;
}

const touch = { minHeight: 44 };

// Readiness is provider-backed: only a real, non-"unavailable" state enables the hosted-onboarding CTA.
// A transport failure (status 0 / 5xx) says nothing about the account, so a known state is kept.
function readinessOutcome(response: { ok: boolean; status: number; data?: any }) {
  if (response.ok && response.data?.state && response.data.state !== 'unavailable') return { kind: 'ok' as const, data: response.data as ConnectReadiness };
  if (response.status === 0 || response.status >= 500) return { kind: 'transient' as const };
  return { kind: 'unavailable' as const };
}

export default function PartnerPortal() {
  const { t } = useTranslation('partner');
  const theme = useTheme();
  const router = useRouter();
  const { userToken, isLoading, login, logout } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [notPartner, setNotPartner] = useState(false);
  // Authenticated, no partner authority yet, but an application awaits review.
  const [applicationPending, setApplicationPending] = useState(false);
  const [summary, setSummary] = useState<PartnerSummary | null>(null);
  const [summaryError, setSummaryError] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [codeDraft, setCodeDraft] = useState('');
  const [choosingCode, setChoosingCode] = useState(false);
  const [codeChoiceError, setCodeChoiceError] = useState(false);
  const codeChoiceInflight = useRef(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const [notice, setNotice] = useState('');
  // Signed-out sign-in: password failure or email-link request outcome.
  const [signInError, setSignInError] = useState('');
  const [linkSent, setLinkSent] = useState(false);
  const [connect, setConnect] = useState<ConnectReadiness | null>(null);
  const [connectUnavailable, setConnectUnavailable] = useState(true);
  const [opening, setOpening] = useState(false);
  const [dashboardOpening, setDashboardOpening] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [fallbackUrl, setFallbackUrl] = useState<string | null>(null);
  const submitting = useRef(false);
  const generation = useRef(0);
  // Liveness + request ordering for the partner summary. `summaryAlive` is set
  // on the auth effect's setup and cleared on its cleanup (account change or
  // unmount); `summarySeq` invalidates any in-flight response that no longer
  // belongs to the current scope. Together they stop a late response for account
  // A from overwriting account B's summary, or from writing after unmount.
  const summaryAlive = useRef(false);
  const summarySeq = useRef(0);
  const connectEpoch = useRef(0);
  const connectInflight = useRef(false);
  const popup = useRef<Window | null>(null);

  const active = workspace?.status === 'active';

  const closePopup = useCallback(() => {
    try { popup.current?.close(); } catch { /* already closed */ }
    popup.current = null;
  }, []);

  const loadSummary = useCallback(async () => {
    // A retained retry after unmount/switch must not start a new wrong-scope read.
    if (!summaryAlive.current) return;
    const seq = ++summarySeq.current;
    setSummaryLoading(true); setSummaryError(false);
    // A transport rejection is a failure outcome, not a stuck spinner.
    let response: { ok: boolean; data?: PartnerSummary };
    try {
      response = await apiFetch<PartnerSummary>(`${API_BASE}/api/referrals/partner/`, { includeCompanyContext: false }, true, false);
    } catch {
      response = { ok: false };
    }
    // The newest request for the still-current account wins; older or out-of-scope
    // responses are discarded without touching summary/loading/error.
    if (!summaryAlive.current || seq !== summarySeq.current) return;
    if (response.ok && response.data) setSummary(response.data); else setSummaryError(true);
    setSummaryLoading(false);
  }, []);

  // `silent` refreshes data in place after an action: nothing is blanked and Connect is left untouched.
  const load = useCallback(async (silent = false) => {
    const epoch = ++generation.current;
    setError(false);
    if (!silent) {
      setLoading(true); setWorkspace(null); setNotPartner(false); setApplicationPending(false);
      setConnect(null); setConnectUnavailable(true);
    }
    const [profile, readiness] = await Promise.all([
      apiFetch<Workspace>(`${API_BASE}/api/referrals/workspace/`, { includeCompanyContext: false }, true, false),
      silent ? Promise.resolve(null)
        : apiFetch<ConnectReadiness>(`${API_BASE}/api/referrals/connect/readiness/`, { includeCompanyContext: false }, true, false),
    ]);
    if (epoch !== generation.current) return;
    const outcome = readiness ? readinessOutcome(readiness) : null;
    if (outcome?.kind === 'ok') { setConnect(outcome.data); setConnectUnavailable(false); }
    if (profile.status === 403) {
      // Authenticated but not (yet) a partner. A pending application is shown as
      // such; anything else (none, rejected, failed read) offers the application journey.
      let pending = false;
      try {
        const application = await apiFetch<{ status?: string | null }>(`${API_BASE}/api/referrals/apply/`, { includeCompanyContext: false }, true, false);
        pending = application.ok && application.data?.status === 'pending';
      } catch { /* keep the application journey */ }
      if (epoch !== generation.current) return;
      if (pending) setApplicationPending(true); else setNotPartner(true);
    } else if (!profile.ok) {
      setError(true);
    } else {
      setWorkspace(profile.data);
      void loadSummary();
    }
    setLoading(false);
  }, [loadSummary]);

  // Read-only readiness check (focus, return from Stripe, manual). It never starts or creates anything.
  const refreshConnect = useCallback(async (manual = false) => {
    if (connectInflight.current) return;
    connectInflight.current = true;
    const epoch = connectEpoch.current;
    setRefreshing(true);
    try {
      const response = await apiFetch<ConnectReadiness>(`${API_BASE}/api/referrals/connect/readiness/`, { includeCompanyContext: false }, true, false);
      if (epoch !== connectEpoch.current) return;
      const outcome = readinessOutcome(response);
      if (outcome.kind === 'ok') { setConnect(outcome.data); setConnectUnavailable(false); }
      else if (outcome.kind === 'unavailable') { setConnect(null); setConnectUnavailable(true); }
      else if (manual) setNotice(t('connectRefreshFailed'));
    } finally { connectInflight.current = false; setRefreshing(false); }
  }, [t]);

  useEffect(() => {
    // New auth scope (including a positive A→B switch, not only logout): re-arm
    // liveness, invalidate any in-flight summary request and drop the previous
    // scope's summary/loading/error so private financial data never persists.
    summaryAlive.current = !!userToken;
    summarySeq.current++;
    setSummary(null); setSummaryError(false); setSummaryLoading(false);
    setCodeDraft(''); setChoosingCode(false); setCodeChoiceError(false);
    codeChoiceInflight.current = false;
    connectEpoch.current++;
    connectInflight.current = false;
    closePopup();
    setFallbackUrl(null);
    setNotice(''); setSignInError(''); setLinkSent(false);
    setConnect(null); setConnectUnavailable(true); setOpening(false); setDashboardOpening(false); setRefreshing(false);
    if (userToken) void load();
    else setWorkspace(null);
    return () => { summaryAlive.current = false; summarySeq.current++; generation.current++; connectEpoch.current++; closePopup(); };
  }, [userToken, load, closePopup]);

  const loaded = !!workspace;
  useEffect(() => {
    if (!userToken || !loaded) return undefined;
    if (Platform.OS === 'web') {
      const onReturn = () => { if (document.visibilityState === 'visible') void refreshConnect(); };
      window.addEventListener('focus', onReturn);
      document.addEventListener('visibilitychange', onReturn);
      return () => { window.removeEventListener('focus', onReturn); document.removeEventListener('visibilitychange', onReturn); };
    }
    const subscription = AppState.addEventListener('change', state => { if (state === 'active') void refreshConnect(); });
    return () => subscription.remove();
  }, [userToken, loaded, refreshConnect]);

  async function run(action: () => Promise<void>) {
    if (submitting.current) return;
    submitting.current = true; setBusy(true); setError(false); setNotice('');
    try { await action(); } catch { setError(true); }
    finally { submitting.current = false; setBusy(false); }
  }

  async function signInWithPassword() {
    setSignInError('');
    await run(async () => {
      const result = await login(email.trim(), password); setPassword('');
      if (!result.success) setSignInError(t('signInFailed'));
    });
  }

  // Passwordless sign-in through the partner access link (generic answer: it
  // never reveals whether the email has an account). The link returns here.
  async function requestEmailLink() {
    setSignInError('');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { setSignInError(t('emailInvalid')); return; }
    await run(async () => {
      try {
        const response = await apiFetch(`${API_BASE}/api/referrals/partner-access/request/`, {
          method: 'POST', includeCompanyContext: false,
          body: JSON.stringify({ email: email.trim(), return_to: 'partner' }),
        }, false, false);
        if (response.status === 400) { setSignInError(t('emailInvalid')); return; }
        // Only a real success claims the link was sent (503 = passwordless off).
        if (!response.ok) { setSignInError(t('emailLinkFailed')); return; }
        setPassword('');
        setLinkSent(true);
      } catch { setSignInError(t('emailLinkFailed')); }
    });
  }

  const shareUrl = safeShareUrl(summary?.share_url, summary?.code ?? undefined);
  // F1: prefill the server suggestion so keeping it is one explicit confirmation.
  useEffect(() => {
    if (summary?.can_choose_code && summary.suggested_code) setCodeDraft(draft => draft || summary.suggested_code!);
  }, [summary?.can_choose_code, summary?.suggested_code]);

  async function chooseInitialCode() {
    if (codeChoiceInflight.current || !active || !summary?.can_choose_code || !codeDraft.trim()) return;
    const epoch = connectEpoch.current;
    codeChoiceInflight.current = true;
    setChoosingCode(true); setCodeChoiceError(false);
    // A GET started before the change must not restore the retired code.
    summarySeq.current++;
    try {
      const response = await apiFetch<PartnerSummary>(`${API_BASE}/api/referrals/partner/code/`, {
        method: 'POST', includeCompanyContext: false, body: JSON.stringify({ code: codeDraft.trim() }),
      }, true, false);
      if (epoch !== connectEpoch.current || !summaryAlive.current) return;
      summarySeq.current++;
      if (!response.ok || !response.data?.code) {
        setCodeChoiceError(true);
        // A lost response or concurrent selection may already have frozen the
        // choice. Read authoritative state before offering another attempt.
        await loadSummary();
        return;
      }
      setSummary(response.data); setCodeDraft('');
    } catch {
      if (epoch === connectEpoch.current && summaryAlive.current) {
        setCodeChoiceError(true);
        await loadSummary();
      }
    } finally {
      if (epoch === connectEpoch.current) {
        codeChoiceInflight.current = false; setChoosingCode(false);
      }
    }
  }

  // The shareable values are the server-owned partner code and its server-composed
  // canonical link, each copied verbatim; nothing is derived client-side.
  async function copyValue(value: string | null | undefined) {
    if (!value || !active) return;
    try {
      if (Platform.OS === 'web') await navigator.clipboard.writeText(value);
      else await Share.share({ message: value });
      setNotice(t('copied'));
    } catch { setNotice(t('copyFailed')); }
  }

  // Hosted Stripe onboarding. The blank tab is opened inside the click gesture (so it is not blocked as an
  // async pop-up), stripped of its opener, and only ever navigated to a validated Stripe Connect HTTPS URL
  // that belongs to the still-current account. The private URL is never rendered, logged or persisted.
  async function startConnect() {
    if (submitting.current) return;
    const epoch = connectEpoch.current;
    popup.current = Platform.OS === 'web' ? window.open('', '_blank') : null;
    await run(async () => {
      setOpening(true);
      try {
        const response = await apiFetch<{ onboarding_url?: string }>(
          `${API_BASE}/api/referrals/connect/start/`,
          { method: 'POST', includeCompanyContext: false, body: JSON.stringify({}) }, true, false,
        );
        if (epoch !== connectEpoch.current) return;
        const url = response.ok ? safeStripeUrl(response.data?.onboarding_url) : null;
        if (!url) {
          if (response.status === 403 || response.status === 503) setConnectUnavailable(true);
          throw new Error('Connect unavailable');
        }
        if (Platform.OS !== 'web') {
          await WebBrowser.openBrowserAsync(url);
          if (epoch === connectEpoch.current) void refreshConnect();
        } else if (popup.current && !popup.current.closed) {
          popup.current.opener = null;
          popup.current.location.replace(url);
          popup.current = null;
        } else setFallbackUrl(url);
      } finally { closePopup(); setOpening(false); }
    });
  }

  // Stripe Express dashboard (balance, transfers, bank payouts). Same safe opening as startConnect: the
  // single-use login link is only navigated when it is a Stripe Connect HTTPS URL, and never rendered,
  // logged or persisted. A failure is a notice, not a Connect outage: readiness stays as last read.
  async function openDashboard() {
    if (submitting.current) return;
    const epoch = connectEpoch.current;
    popup.current = Platform.OS === 'web' ? window.open('', '_blank') : null;
    await run(async () => {
      setDashboardOpening(true);
      try {
        let response: { ok: boolean; status?: number; data?: { url?: string } };
        try {
          response = await apiFetch<{ url?: string }>(
            `${API_BASE}/api/referrals/connect/dashboard/`,
            { method: 'POST', includeCompanyContext: false, body: JSON.stringify({}) }, true, false,
          );
        } catch {
          response = { ok: false };
        }
        if (epoch !== connectEpoch.current) return;
        const url = response.ok ? safeStripeUrl(response.data?.url) : null;
        if (!url) {
          setNotice(t('connectDashboardFailed'));
          if (response.status === 409) void refreshConnect();
          return;
        }
        if (Platform.OS !== 'web') {
          await WebBrowser.openBrowserAsync(url);
        } else if (popup.current && !popup.current.closed) {
          popup.current.opener = null;
          popup.current.location.replace(url);
          popup.current = null;
        } else setFallbackUrl(url);
      } finally { closePopup(); setDashboardOpening(false); }
    });
  }

  function openFallback() {
    const url = safeStripeUrl(fallbackUrl);
    setFallbackUrl(null);
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  }

  const codeCard = <ReferralCodeCard code={summary?.code || null} loading={summaryLoading} error={summaryError} active={active}
    pendingChoice={!summary?.code && summary?.can_choose_code === true}
    suspended={workspace?.status === 'suspended'} terms={workspace?.current_terms} regime={workspace?.vat_regime} onCopy={() => void copyValue(summary?.code)}
    shareUrl={shareUrl} onCopyLink={() => void copyValue(shareUrl)} onRetry={() => void loadSummary()} />;
  const metrics = <MetricsCard summary={summary} loading={summaryLoading} error={summaryError} currency={workspace?.current_terms?.currency ? String(workspace.current_terms.currency) : 'eur'} regime={workspace?.vat_regime} onRetry={() => void loadSummary()} />;
  const payments = <ConnectCard connect={connect} unavailable={connectUnavailable} opening={opening} refreshing={refreshing} busy={busy}
    fallbackReady={!!fallbackUrl} onStart={() => void startConnect()} onRefresh={() => void refreshConnect(true)} onFallback={openFallback}
    dashboardOpening={dashboardOpening} onDashboard={() => void openDashboard()} />;
  const terms = <StandardTerms terms={workspace?.current_terms} regime={workspace?.vat_regime} />;

  return <View style={[styles.root, { backgroundColor: theme.colors.background }]}>
    <ScrollView contentContainerStyle={styles.container}>
      <View style={styles.header}>
        <View testID="partner-brand" accessible accessibilityLabel="AllDrivers"><Logo size="small" /></View>
        {userToken && <Button contentStyle={touch} onPress={() => { generation.current++; connectEpoch.current++; closePopup(); void logout(); }} disabled={busy}>{t('signOut')}</Button>}
      </View>
      <View style={styles.stack}>
        <Text variant="headlineMedium">{t('title')}</Text>
        <Text variant="bodyLarge">{t('subtitle')}</Text>
        {workspace && <View style={styles.actions}>
          <Text variant="titleMedium" style={{ color: theme.colors.onSurfaceVariant }}>{workspace.display_name}</Text>
          {!active && <Chip compact icon="alert-circle-outline">{t('inactiveChip')}</Chip>}
        </View>}
      </View>
      {isLoading ? <ActivityIndicator /> : !userToken ? <Card style={[styles.card, styles.login]}><Card.Content style={styles.stack}>
        {linkSent ? <>
          <Text accessibilityRole="alert" testID="partner-link-sent">{t('emailLinkSentTitle')}</Text>
          <Text variant="bodyMedium">{t('emailLinkSentHelp')}</Text>
          <Button contentStyle={touch} testID="partner-link-back" onPress={() => setLinkSent(false)}>{t('emailLinkBack')}</Button>
        </> : <>
          <TextInput label={t('email')} accessibilityLabel={t('email')} value={email} onChangeText={setEmail} autoCapitalize="none" keyboardType="email-address" autoComplete="email" testID="partner-email" />
          <TextInput label={t('password')} accessibilityLabel={t('password')} value={password} onChangeText={setPassword} secureTextEntry autoComplete="current-password" testID="partner-password" />
          {!!signInError && <Text accessibilityRole="alert" style={{ color: theme.colors.error }}>{signInError}</Text>}
          <Button mode="contained" contentStyle={touch} loading={busy} disabled={busy || !email || !password} testID="partner-sign-in" onPress={() => void signInWithPassword()}>{t('signIn')}</Button>
          <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>{t('emailLinkHelp')}</Text>
          <Button mode="outlined" icon="email-outline" contentStyle={touch} disabled={busy || !email} testID="partner-email-link" onPress={() => void requestEmailLink()}>{t('emailLinkSignIn')}</Button>
          <Button contentStyle={touch} disabled={busy} testID="partner-open-apply" onPress={() => router.push('/partner-apply' as never)}>{t('notPartnerYet')}</Button>
        </>}
      </Card.Content></Card> : <>
        {notPartner && <Card testID="partner-apply-card" style={styles.card}><Card.Content style={styles.stack}>
          <Text variant="titleLarge">{t('applyTitle')}</Text>
          <Text>{t('applyIntro')}</Text>
          <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>{t('applyDistinct')}</Text>
          <Button mode="contained" icon="account-plus" contentStyle={touch} testID="open-apply" onPress={() => router.push('/partner-apply' as never)}>{t('applySubmit')}</Button>
        </Card.Content></Card>}
        {applicationPending && <Card testID="partner-pending-card" style={styles.card}><Card.Content style={styles.stack}>
          <Text variant="titleLarge" accessibilityRole="header">{t('applyReviewTitle')}</Text>
          <Text>{t('applyPending')}</Text>
          <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>{t('applyPendingHelp')}</Text>
        </Card.Content></Card>}
        {loading && <ActivityIndicator />}
        {error && <View style={styles.stack}><Text accessibilityRole="alert" style={{ color: theme.colors.error }}>{t('error')}</Text><Button contentStyle={touch} onPress={() => void load(!!workspace)} disabled={busy}>{t('retry')}</Button></View>}
        {workspace && <>
          {!active && <Text accessibilityRole="alert" style={{ color: theme.colors.error }}>{t('inactiveNotice')}</Text>}
          <View testID="partner-column" style={styles.stackLarge}>
            {codeCard}
            {active && !summaryLoading && !summaryError && summary?.can_choose_code === true && <Card style={styles.card}>
              <Card.Content style={styles.stack}>
                <Text variant="titleMedium">{t('initialCodeTitle')}</Text>
                <Text>{t('initialCodeHelp')}</Text>
                <TextInput testID="partner-code-choice-input" label={t('initialCodeLabel')} value={codeDraft}
                  onChangeText={setCodeDraft} autoCapitalize="characters" maxLength={32} disabled={choosingCode} />
                {codeChoiceError && <Text accessibilityRole="alert">{t('initialCodeError')}</Text>}
                <Button testID="partner-code-choice-submit" mode="contained" contentStyle={touch}
                  loading={choosingCode} disabled={choosingCode || !codeDraft.trim()} onPress={() => void chooseInitialCode()}>{t('initialCodeConfirm')}</Button>
              </Card.Content>
            </Card>}
            {metrics}{payments}{terms}
          </View>
        </>}
      </>}
    </ScrollView>
    <Snackbar visible={!!notice} onDismiss={() => setNotice('')} duration={3000}>{notice}</Snackbar>
  </View>;
}

const styles = StyleSheet.create({
  root: { flex: 1 },
  container: { padding: 16, gap: 16, width: '100%', maxWidth: 860, alignSelf: 'center', paddingBottom: 40 },
  header: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  card: { borderRadius: 16 }, login: { maxWidth: 480, width: '100%', alignSelf: 'center' },
  stack: { gap: 12 }, stackLarge: { gap: 16 },
  actions: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, alignItems: 'center' },
});
